import type {
  SeoAttributionInput,
  SeoMilestoneName,
} from "@/content/seo/types";
import { createServiceClient } from "@/utils/supabase/service";

const SAFE_TOKEN = /^[a-z0-9][a-z0-9-]{0,79}$/i;
const SAFE_LOCALE = /^[a-z]{2}(?:-[a-z]{2})?$/i;
const SAFE_PATH = /^\/(?!\/)[^\s?#]{0,239}$/;

const cleanToken = (value: unknown, fallback: string) => {
  const normalized = String(value || "").trim();
  return SAFE_TOKEN.test(normalized) ? normalized : fallback;
};

const cleanLandingPath = (value: unknown) => {
  const normalized = String(value || "").trim();
  return SAFE_PATH.test(normalized) ? normalized : "/";
};

export function normalizeSeoAttribution(
  value: Partial<SeoAttributionInput> | null | undefined,
): SeoAttributionInput | null {
  if (!value || value.intent !== "faceless-video") return null;

  const locale = String(value.locale || "en").trim().toLowerCase();

  return {
    source: cleanToken(value.source, "direct"),
    landingPath: cleanLandingPath(value.landingPath),
    ctaLocation: cleanToken(value.ctaLocation, "unknown"),
    locale: SAFE_LOCALE.test(locale) ? locale : "en",
    intent: "faceless-video",
  };
}

export function seoAttributionFromSearchParams(
  searchParams: URLSearchParams,
): SeoAttributionInput | null {
  return normalizeSeoAttribution({
    source: searchParams.get("source") || undefined,
    landingPath: searchParams.get("landingPath") || undefined,
    ctaLocation: searchParams.get("location") || undefined,
    locale: searchParams.get("locale") || undefined,
    intent:
      searchParams.get("intent") === "faceless-video"
        ? "faceless-video"
        : undefined,
  });
}

export async function recordFirstTouchAttribution({
  userId,
  attribution,
  signupCompleted = false,
}: {
  userId: string;
  attribution: SeoAttributionInput;
  signupCompleted?: boolean;
}) {
  const normalized = normalizeSeoAttribution(attribution);
  if (!normalized) return;

  const supabase = createServiceClient();
  const now = new Date().toISOString();
  const { error: attributionError } = await supabase
    .from("seo_user_attribution")
    .upsert(
      {
        user_id: userId,
        source: normalized.source,
        landing_path: normalized.landingPath,
        cta_location: normalized.ctaLocation,
        locale: normalized.locale,
        intent: normalized.intent,
        first_touch_at: now,
        signup_completed_at: signupCompleted ? now : null,
      },
      {
        onConflict: "user_id",
        ignoreDuplicates: true,
      },
    );

  if (attributionError) throw attributionError;

  if (signupCompleted) {
    const { error: completionError } = await supabase
      .from("seo_user_attribution")
      .update({ signup_completed_at: now })
      .eq("user_id", userId)
      .is("signup_completed_at", null);

    if (completionError) throw completionError;
    await recordSeoMilestone({
      userId,
      eventName: "signup_completed",
    });
  }
}

export async function recordSeoMilestone({
  userId,
  eventName,
  entityId,
}: {
  userId: string;
  eventName: SeoMilestoneName;
  entityId?: string | null;
}) {
  const supabase = createServiceClient();
  const { error } = await supabase.rpc(
    "record_seo_conversion_milestone",
    {
      p_user_id: userId,
      p_event_name: eventName,
      p_entity_id: entityId ? String(entityId).slice(0, 160) : null,
      p_occurred_at: new Date().toISOString(),
    },
  );

  if (error) throw error;
}
